import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert, Grid,
  Switch, Rating, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import adminApi from "../api/adminApi";

// The website shows the reviews switched on here, in this order (at most 6).
const MAX_ON_SITE = 6;
const EMPTY_FORM = { author: "", text: "", rating: 5, publishedAt: "" };

const ReviewDialog = ({ open, onClose, review, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(
      review
        ? { author: review.author, text: review.text, rating: review.rating, publishedAt: review.publishedAt?.slice(0, 10) || "" }
        : EMPTY_FORM
    );
    setError("");
  }, [review, open]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (review?._id) await adminApi.put(`/reviews/${review._id}`, form);
      else await adminApi.post("/reviews", form);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{review?._id ? "Edit Review" : "Add Review"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={8}><TextField label="Author (as shown on the site)" fullWidth value={form.author} onChange={set("author")} /></Grid>
          <Grid item xs={4}><TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={form.publishedAt} onChange={set("publishedAt")} /></Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Rating</Typography>
            <Rating value={Number(form.rating)} onChange={(e, v) => v && setForm((p) => ({ ...p, rating: v }))} />
          </Grid>
          <Grid item xs={12}><TextField label="Review text" fullWidth multiline rows={5} value={form.text} onChange={set("text")} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await adminApi.get("/reviews/all");
    setReviews(data.items);
    setSummary(data.summary);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const shown = reviews.filter((r) => r.visible).sort((a, b) => a.order - b.order);

  const update = async (id, changes) => {
    await adminApi.put(`/reviews/${id}`, changes);
    await load();
  };

  // Shown reviews get positions 1..n; moving one swaps it with its neighbour.
  const move = async (review, direction) => {
    const list = [...shown];
    const i = list.findIndex((r) => r._id === review._id);
    const j = i + direction;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    await Promise.all(list.map((r, idx) => adminApi.put(`/reviews/${r._id}`, { order: idx + 1 })));
    await load();
  };

  const toggle = async (review) => {
    const visible = !review.visible;
    await update(review._id, visible ? { visible, order: shown.length + 1 } : { visible });
  };

  const sync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const { data } = await adminApi.post("/reviews/sync");
      setMessage({ severity: "success", text: `Google: ${data.added} new, ${data.updated} updated. New reviews start hidden.` });
      await load();
    } catch (err) {
      setMessage({ severity: "error", text: err.response?.data?.message || "Sync failed" });
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review? A Google review comes back hidden on the next sync.")) return;
    await adminApi.delete(`/reviews/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight="bold" color="#1C2541">Reviews</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />} onClick={sync} disabled={syncing}>
            Sync from Google
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDialogOpen(true); }} sx={{ backgroundColor: "#1C2541" }}>
            Add Review
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {summary?.score
          ? `Google rating ${summary.score} / 5 from ${summary.count} reviews (last sync ${new Date(summary.syncedAt).toLocaleString()}).`
          : "No Google sync yet."}{" "}
        The website shows the switched-on reviews, in order, up to {MAX_ON_SITE} ({shown.length} on now).
      </Typography>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {shown.length > MAX_ON_SITE && (
        <Alert severity="warning" sx={{ mb: 2 }}>Only the first {MAX_ON_SITE} are shown on the website.</Alert>
      )}

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>On site</strong></TableCell>
              <TableCell><strong>Order</strong></TableCell>
              <TableCell><strong>Rating</strong></TableCell>
              <TableCell><strong>Author</strong></TableCell>
              <TableCell><strong>Review</strong></TableCell>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...shown, ...reviews.filter((r) => !r.visible)].map((r) => {
              const position = r.visible ? shown.findIndex((s) => s._id === r._id) : -1;
              return (
                <TableRow key={r._id} hover sx={{ opacity: r.visible ? 1 : 0.65 }}>
                  <TableCell><Switch checked={r.visible} onChange={() => toggle(r)} /></TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {r.visible ? (
                      <>
                        {position + 1}
                        <IconButton size="small" disabled={position === 0} onClick={() => move(r, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" disabled={position === shown.length - 1} onClick={() => move(r, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell><Rating value={r.rating} readOnly size="small" /></TableCell>
                  <TableCell>{r.author}</TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Tooltip title={r.text}><Typography variant="body2" noWrap>{r.text}</Typography></Tooltip>
                  </TableCell>
                  <TableCell><Chip label={r.source === "google" ? "Google" : "Manual"} size="small" color={r.source === "google" ? "primary" : "default"} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {r.source === "manual" && (
                      <IconButton size="small" onClick={() => { setSelected(r); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => handleDelete(r._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {!reviews.length && (
              <TableRow><TableCell colSpan={7} align="center">No reviews yet: sync from Google or add one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ReviewDialog open={dialogOpen} onClose={() => setDialogOpen(false)} review={selected} onSaved={load} />
    </Box>
  );
};

export default ReviewsManager;
